import {
  Box,
  Button,
  ButtonGroup,
  Icon,
  List,
  ListItem,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { FlowType, MembershipLevel } from '@ace-clublabs/common-membership-api-types';
import { NewJoinSessionData } from '../../application/models/NewJoinSessionData';
import { benefitListTitleMap, stringHelper } from '../../utils/stringHelper';
import { IAdditionalBenefits } from '@lib/types';
import { IconName } from '@ace-clublabs/shared-component-library/dist/components/Icons';

export interface LocationState {
  isUpgrading?: boolean;
  redirectPath?: string;
  previousMembershipLevel: MembershipLevel;
}

interface IMembershipBenefitProps {
  sessionData: NewJoinSessionData;
  additionalBenefits: IAdditionalBenefits | undefined;
  membershipLevel: MembershipLevel;
  onContinue?: () => void;
  isUpgrading?: boolean;
  admissionFee: number;
  admissionDiscount: number;
  allowedMembershipLevels?: MembershipLevel[] | undefined;
}

export const MembershipBenefit = ({
  sessionData,
  additionalBenefits,
  membershipLevel: selectedMembershipLevel,
  onContinue,
  isUpgrading,
  admissionFee,
  admissionDiscount,
  allowedMembershipLevels,
}: IMembershipBenefitProps) => {
  const sections = [additionalBenefits?.['group1'], additionalBenefits?.['group2']].filter(Boolean);

  const previousMembershipLevel = sessionData.formData.membershipLevel ?? MembershipLevel.CLASSIC;
  const hasAdmissionDiscount = admissionDiscount > 0;
  const admissionFeeAfterDiscount = hasAdmissionDiscount && admissionFee - admissionDiscount;
  const admissionWaived = admissionFee === admissionDiscount;
  const isGrouponFlow = sessionData.flowType === FlowType.GROUPON;
  const iconsNames = ['truck-tow-duotone', 'circle-plus-duotone'];

  const labels = allowedMembershipLevels?.map((memLevel) => benefitListTitleMap[memLevel]) || [];

  const largestLength = Math.max(...labels.map((str) => str.length));
  const largestLabel = labels.find((label) => label.length === largestLength);

  const benefitTitle =
    labels.length > 1 ? (
      <Typography
        variant="b1Bold"
        className="benefit-list-title"
        sx={{ position: 'relative', display: 'inline-block' }}
      >
        <Box component="span" sx={{ position: 'absolute', top: 0, left: 0 }}>
          {isGrouponFlow ? benefitListTitleMap[MembershipLevel.CLASSIC] : benefitListTitleMap[selectedMembershipLevel]}
        </Box>
        <Box component="span" sx={{ visibility: 'hidden', opacity: 0 }}>
          {largestLabel}
        </Box>
      </Typography>
    ) : (
      <Typography variant="b1Bold" className="benefit-list-title">
        {isGrouponFlow ? benefitListTitleMap[MembershipLevel.CLASSIC] : benefitListTitleMap[selectedMembershipLevel]}
      </Typography>
    );

  const sectionListFragment = (
    <>
      {benefitTitle}
      {sections.map((section, index) => (
        <span key={index}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: '500', color: '#0C7A93', marginBottom: '0.5rem' }}
            className={`section${index + 1}-title`}
          >
            <Icon
              iconName={iconsNames[index] as IconName}
              sx={{ fontSize: '1.25rem', marginTop: '0.25rem', marginRight: '0.25rem' }}
            ></Icon>

            {section?.title}
          </Typography>
          <List sx={{ marginTop: '-0.25rem' }}>
            {section?.benefits.map((benefit: any, i: any) => (
              <ListItem sx={{ marginLeft: '0.35rem' }} className="benefit-item-container" key={'ben-' + i}>
                <Typography component={'span'} sx={{ alignSelf: 'baseline' }}>
                  {'\u2022'}
                </Typography>
                <Typography component={'span'} variant="b2Regular">
                  <span dangerouslySetInnerHTML={{ __html: benefit }} />
                </Typography>
              </ListItem>
            ))}
          </List>
        </span>
      ))}
    </>
  );

  return (
    <Stack className="benefit-card-and-description" flexDirection="column" gap="0.5rem" width={'100%'}>
      <Stack
        className="admission-fee-container"
        direction="row"
        sx={(theme) => ({
          alignSelf: 'self-start',
          marginTop: '1rem',
          [theme.breakpoints.up('lg')]: {
            alignSelf: 'center',
            marginTop: 0,
          },
        })}
      >
        {admissionFee ? (
          <Typography id="admission-fee" variant="b1Regular">
            + One-time admission fee:{' '}
            <Typography
              component="span"
              className={hasAdmissionDiscount ? 'strikethrough' : 'admission-fee'}
              marginRight={'0.2rem'}
            >
              {stringHelper.formatMoney(admissionFee.toString())}{' '}
            </Typography>
            <Typography component="span" fontWeight={700} color="#3E6B20">
              {admissionWaived ? 'Waived' : `${stringHelper.formatMoney(admissionFeeAfterDiscount.toString())}`}
            </Typography>
          </Typography>
        ) : (
          <></>
        )}
      </Stack>
      <ButtonGroup
        className="benefit-buttons"
        width="100%"
        paddingTop="1rem"
        paddingBottom="1rem"
        gap="1rem !important"
      >
        <Button
          label={getBenefitButtonText(selectedMembershipLevel, Boolean(isUpgrading), previousMembershipLevel)}
          fullWidth
          onClick={onContinue}
          variant="contained"
          size="medium"
          id="getMembershipButton"
        ></Button>
      </ButtonGroup>
      <Box className="benefit-list-container" marginBottom="1.5rem">
        <Stack className="benefit-detail-list" flexDirection="column" gap="0.5rem">
          {' '}
          {sectionListFragment}
        </Stack>
      </Box>
    </Stack>
  );
};

const UPGRADE_DOWNGRADE_KEEP_MAP = {
  [MembershipLevel.CLASSIC]: {
    [MembershipLevel.CLASSIC]: 'Keep',
    [MembershipLevel.PLUS]: 'Upgrade',
    [MembershipLevel.PREMIER]: 'Upgrade',
  },
  [MembershipLevel.PLUS]: {
    [MembershipLevel.CLASSIC]: 'Downgrade',
    [MembershipLevel.PLUS]: 'Keep',
    [MembershipLevel.PREMIER]: 'Upgrade',
  },
  [MembershipLevel.PREMIER]: {
    [MembershipLevel.CLASSIC]: 'Downgrade',
    [MembershipLevel.PLUS]: 'Downgrade',
    [MembershipLevel.PREMIER]: 'Keep',
  },
};

const getBenefitButtonText = (
  selectedMembershipLevel: MembershipLevel,
  isUpgrading: boolean,
  previousMembershipLevel: MembershipLevel
): string => {
  if (isUpgrading == true && previousMembershipLevel in MembershipLevel) {
    // @ts-ignore
    return UPGRADE_DOWNGRADE_KEEP_MAP[previousMembershipLevel][selectedMembershipLevel];
  }
  return 'Join now';
};
